import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import { motion } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher'
import { useAuth } from '@/contexts/AuthContext'
import { useUiLocale } from '@/contexts/UiLocaleContext'
import { cn } from '@/lib/utils'

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, completeTwoFactorChallenge, cancelTwoFactor, twoFactorPending } = useAuth()
  const { t } = useUiLocale()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(false)
  const [code, setCode] = useState('')
  const [recoveryCode, setRecoveryCode] = useState('')
  const [useRecoveryCode, setUseRecoveryCode] = useState(false)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const redirectTo = location.state?.from?.pathname ?? '/'

  async function handleLoginSubmit(event) {
    event.preventDefault()
    setError('')
    setSubmitting(true)

    try {
      const result = await login(email, password, remember)
      if (!result.twoFactorRequired) {
        navigate(redirectTo, { replace: true })
      }
    } catch (err) {
      const message =
        getFieldError(err, 'email') ??
        err?.response?.data?.message ??
        (err?.response
          ? 'Unable to sign in. Check your credentials.'
          : 'Cannot reach the API. Confirm the backend is running (http://localhost:8002) and refresh the page.')
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleTwoFactorSubmit(event) {
    event.preventDefault()
    setError('')
    setSubmitting(true)

    try {
      await completeTwoFactorChallenge({
        code: useRecoveryCode ? undefined : code,
        recoveryCode: useRecoveryCode ? recoveryCode : undefined,
      })
      navigate(redirectTo, { replace: true })
    } catch (err) {
      const message =
        getFieldError(err, 'code') ??
        err?.response?.data?.message ??
        'Invalid authentication code.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="luxury-mesh flex min-h-full items-center justify-center p-4">
      <motion.div
        className="glass-luxury w-full max-w-md rounded-3xl p-8 shadow-2xl"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-[hsl(262_70%_48%)] text-white shadow-md">
            <Sparkles className="h-6 w-6" aria-hidden />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Lumière</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Ladies Spa · Dubai Marina
          </p>
          <LanguageSwitcher className="mt-4" />
        </div>

        {twoFactorPending ? (
          <form onSubmit={handleTwoFactorSubmit} className="space-y-4">
            <div className="text-center">
              <h2 className="text-lg font-semibold">{t('auth.twoFactorTitle')}</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {useRecoveryCode ? t('auth.twoFactorHintRecovery') : t('auth.twoFactorHint')}
              </p>
            </div>

            {useRecoveryCode ? (
              <div>
                <label htmlFor="recovery-code" className="sr-only">
                  {t('auth.recoveryCode')}
                </label>
                <input
                  id="recovery-code"
                  type="text"
                  autoComplete="off"
                  autoFocus
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  placeholder={t('auth.recoveryCode')}
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                />
              </div>
            ) : (
              <div>
                <label htmlFor="totp-code" className="sr-only">
                  {t('auth.authCode')}
                </label>
                <input
                  id="totp-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="6-digit code"
                  className={cn(
                    'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm tracking-widest',
                    'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                />
              </div>
            )}

            {error ? (
              <p className="text-center text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}

            <Button
              type="submit"
              className="w-full"
              disabled={
                submitting || (useRecoveryCode ? !recoveryCode.trim() : !code.trim())
              }
            >
              {submitting ? t('auth.verifying') : t('auth.continue')}
            </Button>

            <div className="flex flex-col gap-2 text-center text-sm">
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => {
                  setUseRecoveryCode((value) => !value)
                  setError('')
                  setCode('')
                  setRecoveryCode('')
                }}
              >
                {useRecoveryCode ? t('auth.useAuthenticatorCode') : t('auth.useRecoveryCode')}
              </button>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => {
                  cancelTwoFactor()
                  setError('')
                  setCode('')
                  setRecoveryCode('')
                  setUseRecoveryCode(false)
                }}
              >
                {t('auth.backToSignIn')}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleLoginSubmit} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="mb-1.5 block text-sm font-medium">
                {t('auth.email')}
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@lumiere.test"
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>

            <div>
              <label htmlFor="login-password" className="mb-1.5 block text-sm font-medium">
                {t('auth.password')}
              </label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="rounded border-border"
              />
              {t('auth.rememberMe')}
            </label>

            {error ? (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}

            <Button type="submit" className="w-full" disabled={submitting || !email || !password}>
              {submitting ? t('auth.signingIn') : t('auth.signIn')}
            </Button>

            <p className="text-center text-[11px] text-muted-foreground">
              Demo: manager@lumiere.test / password
            </p>
          </form>
        )}
      </motion.div>
    </div>
  )
}
